import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  ExternalLink, 
  RefreshCw, 
  Play, 
  Loader2, 
  Globe, 
  CheckCircle,
  MoreVertical,
  DollarSign
} from 'lucide-react';
import { Scrape } from '@/helpers/scrapeDatabase';
import { getStatusBadgeVariant, getVerificationBadgeVariant, getVerificationBadgeText } from '@/helpers/scrapeUtils';

interface ScrapesTableProps {
  scrapes: Scrape[];
  loading: boolean;
  selectedScrapes: string[];
  verifyingScrapesIds: string[];
  rerunningScrapesIds: string[];
  updatingScraperIds: string[];
  updatingPricingUrlIds: string[];
  updatingNoPricingIds: string[];
  updatingVerifiedIds: string[];
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  onScrapeSelection: (scrapeId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onVerifyScreenshot: (scrape: Scrape) => void;
  onRerunScrape: (scrape: Scrape) => void;
  onScraperChange: (scrape: Scrape, newScraper: string) => void;
  onEditPricingUrl: (scrape: Scrape) => void;
  onNoPricingToggle: (scrape: Scrape, noPricing: boolean) => Promise<void>;
  onVerifiedToggle: (scrape: Scrape) => Promise<void>;
}

export const ScrapesTable: React.FC<ScrapesTableProps> = ({
  scrapes,
  loading,
  selectedScrapes,
  verifyingScrapesIds,
  rerunningScrapesIds,
  updatingScraperIds,
  updatingPricingUrlIds,
  updatingNoPricingIds,
  updatingVerifiedIds,
  sortColumn,
  sortDirection,
  onSort,
  onScrapeSelection,
  onSelectAll,
  onVerifyScreenshot,
  onRerunScrape,
  onScraperChange,
  onEditPricingUrl,
  onNoPricingToggle,
  onVerifiedToggle,
}) => {
  const renderLink = (url: string | null, label: string) => {
    if (!url) return 'N/A';

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
      >
        {label}
        <ExternalLink size={14} />
      </a>
    );
  };

  const renderSortableHeader = (column: string, label: string) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-50"
      onClick={() => onSort(column)}
    >
      {label}
      {sortColumn === column && (
        <span className="ml-1">
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
    </TableHead>
  );

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={scrapes.length > 0 && selectedScrapes.length === scrapes.length}
                onCheckedChange={onSelectAll}
                aria-label="Select all scrapes"
              />
            </TableHead>
            <TableHead className="w-40">
              {renderSortableHeader("slug", "Company")}
            </TableHead>
            <TableHead className="w-24">
              {renderSortableHeader("status", "Status")}
            </TableHead>
            <TableHead className="w-32">
              {renderSortableHeader("timestamp", "Last Run")}
            </TableHead>
            <TableHead className="w-28">Scraper</TableHead>
            <TableHead className="w-24 text-center">
              {renderSortableHeader("verified", "Verified")}
            </TableHead>
            <TableHead className="w-32">URL</TableHead>
            <TableHead className="w-20 text-center">Preview</TableHead>
            <TableHead className="w-32">Resources</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={12} className="text-center py-8">
                <div className="flex justify-center">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
                <p className="text-gray-500 mt-2">Loading scrapes...</p>
              </TableCell>
            </TableRow>
          ) : scrapes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} className="text-center py-8">
                <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-8 rounded text-center">
                  No scrapes found for any company.
                </div>
              </TableCell>
            </TableRow>
          ) : (
            scrapes.map((scrape) => (
              <TableRow key={scrape.slug}>
                <TableCell>
                  <Checkbox
                    checked={selectedScrapes.includes(scrape.slug)}
                    onCheckedChange={(checked) => onScrapeSelection(scrape.slug, checked as boolean)}
                    aria-label={`Select ${scrape.slug}`}
                  />
                </TableCell>
                <TableCell className="font-medium w-32 max-w-32 truncate" title={scrape.slug}>{scrape.slug}</TableCell>
                <TableCell className="w-28 max-w-28">
                  <Badge variant={getStatusBadgeVariant(scrape.status) as any}>
                    {scrape.status}
                  </Badge>
                  {scrape.last_error && scrape.status === 'error' && (
                    <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={scrape.last_error}>
                      {scrape.last_error}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {new Date(scrape.timestamp).toLocaleString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {updatingScraperIds.includes(scrape.slug) ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          Updating...
                        </Badge>
                        <Loader2 className="h-3 w-3 animate-spin" />
                      </div>
                    ) : (
                      <Select
                        value={scrape.scraper || 'default'}
                        onValueChange={(value) => onScraperChange(scrape, value)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default</SelectItem>
                          <SelectItem value="scrapingBee">ScrapingBee</SelectItem>
                          <SelectItem value="firecrawl">Firecrawl</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    {renderLink(scrape.url, 'Original')}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditPricingUrl(scrape)}
                      disabled={updatingPricingUrlIds.includes(scrape.slug)}
                      className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                    >
                      {updatingPricingUrlIds.includes(scrape.slug) ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Globe className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  {renderLink(scrape.cloudinary_url || scrape.screenshot_url, 'Screenshot')}
                </TableCell>
                <TableCell>
                  <div 
                    className={`cursor-pointer ${!scrape.verified ? 'hover:opacity-80' : ''}`}
                    onClick={() => !scrape.verified && onVerifiedToggle(scrape)}
                    title={!scrape.verified ? 'Click to mark as verified' : 'Already verified'}
                  >
                    <Badge 
                      variant={getVerificationBadgeVariant(scrape.verified) as any}
                      className={updatingVerifiedIds.includes(scrape.slug) ? 'opacity-50' : ''}
                    >
                      {updatingVerifiedIds.includes(scrape.slug) ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Updating...
                        </>
                      ) : (
                        getVerificationBadgeText(scrape.verified)
                      )}
                    </Badge>
                  </div>
                  {scrape.verification_details && (
                    <div className="text-xs text-gray-600 mt-1 max-w-xs truncate" title={JSON.stringify(scrape.verification_details)}>
                      {JSON.stringify(scrape.verification_details)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onRerunScrape(scrape)}
                        disabled={rerunningScrapesIds.includes(scrape.slug)}
                      >
                        {rerunningScrapesIds.includes(scrape.slug) ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Rerun
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onVerifyScreenshot(scrape)}
                        disabled={verifyingScrapesIds.includes(scrape.slug) || (!scrape.cloudinary_url && !scrape.screenshot_url)}
                      >
                        {verifyingScrapesIds.includes(scrape.slug) ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Verify
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onNoPricingToggle(scrape, !scrape.no_pricing)}
                        disabled={updatingNoPricingIds.includes(scrape.slug)}
                      >
                        {updatingNoPricingIds.includes(scrape.slug) ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-4 w-4 mr-2" />
                            {scrape.no_pricing ? 'Mark Has Pricing' : 'Mark No Pricing'}
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
                <TableCell>
                  {renderLink(scrape.html_url, 'HTML')}
                </TableCell>
                <TableCell>
                  {renderLink(scrape.markdown_url, 'Markdown')}
                </TableCell>
                <TableCell>
                  {renderLink(scrape.cloudinary_url, 'Cloudinary')}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
